/**
 * AUT — Workspace Lookup (app.clobi.io)
 *
 * Entry point sin tenant. El usuario que no sabe su subdominio ingresa su
 * email y recibe la lista de workspaces a los que pertenece. Cada item lleva
 * al login del tenant correspondiente.
 *
 * La respuesta del endpoint es genérica (nunca revela si el email existe),
 * por lo que la UI siempre muestra el mismo estado "resuelto" — si la lista
 * viene vacía, se invita al usuario a revisar el email o contactar al admin.
 */
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Building2, ExternalLink, CheckCircle2 } from 'lucide-react'
import { useWorkspaceLookup, type WorkspaceLookupResult } from '@/modules/auth/hooks/useAuth'
import { Button } from '@/components/Button'

const schema = z.object({
  email: z.string().min(1, 'El email es requerido').email('Falta un email válido'),
})
type FormData = z.infer<typeof schema>

function buildTenantUrl(domain: string, path: string = '/'): string {
  const { protocol, port } = window.location
  const portPart = port && port !== '80' && port !== '443' ? `:${port}` : ''
  const safePath = path.startsWith('/') ? path : `/${path}`
  return `${protocol}//${domain}${portPart}${safePath}`
}

function readSessionWorkspaces(): WorkspaceLookupResult[] | null {
  const match = document.cookie.match(/(?:^|;\s*)zen_session=([^;]+)/)
  if (!match) return null
  try {
    const session = JSON.parse(atob(decodeURIComponent(match[1])))
    if (!session?.isAuthenticated || !Array.isArray(session.tenants)) return null
    const list = session.tenants
      .filter((t: { domain?: string | null }) => !!t.domain)
      .map((t: { schema: string; name: string; domain: string }) => ({
        schema: t.schema,
        name: t.name,
        domain: t.domain,
      }))
    return list.length > 0 ? list : null
  } catch {
    return null
  }
}

export function WorkspaceLookupPage() {
  const { mutate, isPending, data, reset } = useWorkspaceLookup()
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [sessionWorkspaces, setSessionWorkspaces] = useState<WorkspaceLookupResult[] | null>(null)
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    const list = readSessionWorkspaces()
    if (!list) return
    const next = new URLSearchParams(window.location.search).get('next') || '/'
    if (list.length === 1) {
      setRedirecting(true)
      window.location.replace(buildTenantUrl(list[0].domain, next))
      return
    }
    setSessionWorkspaces(list)
  }, [])

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = (values: FormData) => {
    setSubmittedEmail(values.email)
    mutate(values.email)
  }

  const showResult = !!data
  const hasSession = sessionWorkspaces !== null

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-auth px-4">
        <div className="text-white text-sm">Redirigiendo a tu workspace…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-auth px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/clobi-logo-white.svg" alt="Clobi" className="h-32 mx-auto mb-4" />
          <p className="text-content-subtle mt-1 text-sm">Renace tu empresa. Opera en la nube.</p>
        </div>

        <div className="bg-surface rounded-2xl shadow-2xl p-8">
          {hasSession ? (
            <>
              <div className="flex items-start gap-3 mb-6">
                <CheckCircle2 size={24} className="text-success-solid shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-semibold text-content">
                    Elige tu workspace
                  </h2>
                  <p className="text-sm text-content-muted">
                    Tienes una sesión activa. Selecciona el workspace al que quieres entrar.
                  </p>
                </div>
              </div>

              <ul className="space-y-2">
                {sessionWorkspaces!.map((ws) => {
                  const next = new URLSearchParams(window.location.search).get('next') || '/'
                  return (
                    <li key={ws.schema}>
                      <a
                        href={buildTenantUrl(ws.domain, next)}
                        className="flex items-center gap-3 p-3 rounded-lg border border-divider hover:border-primary hover:bg-primary-50 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2 size={18} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-content truncate">{ws.name}</p>
                          <p className="text-xs text-content-muted truncate">{ws.domain}</p>
                        </div>
                        <ExternalLink size={14} className="text-content-subtle group-hover:text-primary shrink-0" />
                      </a>
                    </li>
                  )
                })}
              </ul>
            </>
          ) : !showResult ? (
            <>
              <h2 className="text-xl font-semibold text-content mb-2">
                Encuentra tu workspace
              </h2>
              <p className="text-sm text-content-muted mb-6">
                Ingresa tu email y te enviaremos por correo las empresas a las que tienes acceso.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-content mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
                    <input
                      {...register('email')}
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="tu@empresa.com"
                      className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-content placeholder:text-content-subtle
                        text-sm transition-colors outline-none
                        focus:ring-2 focus:ring-primary focus:border-primary
                        ${errors.email
                          ? 'border-danger-border-strong bg-danger-surface focus:ring-danger-solid focus:border-danger-solid'
                          : 'border-divider-strong bg-surface hover:border-divider-strong'
                        }`}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs text-danger-content-soft">{errors.email.message}</p>
                  )}
                </div>

                <Button type="submit" size="lg" fullWidth isPending={isPending} pendingLabel="Buscando...">
                  Buscar workspaces
                </Button>
              </form>

              <p className="text-xs text-content-subtle mt-6 text-center">
                ¿Ya sabes tu workspace?{' '}
                <a
                  href={`${window.location.protocol}//clobi.io/auth/login`}
                  className="text-primary hover:text-primary-hover hover:underline"
                >
                  Ve al login
                </a>
              </p>
            </>
          ) : (
            // MED-2: no se revela si el email existe ni la lista de workspaces.
            // Si está registrado, la lista llega por correo.
            <div className="text-center py-4">
              <Mail size={48} className="mx-auto text-content-subtle mb-4" />
              <h2 className="text-lg font-semibold text-content mb-2">Revisa tu correo</h2>
              <p className="text-sm text-content-muted mb-6">
                Si <span className="font-medium text-content">{submittedEmail}</span> está
                registrado, te enviamos un correo con los espacios de trabajo a los que tienes
                acceso. Revisa tu bandeja de entrada (y la carpeta de spam).
              </p>
              <button
                type="button"
                onClick={() => { reset(); setSubmittedEmail('') }}
                className="text-sm text-primary hover:text-primary-hover hover:underline"
              >
                ← Intentar con otro email
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-content-muted text-xs mt-6">
          © 2026 Clobi by zen · Todos los derechos reservados
        </p>
      </div>
    </div>
  )
}
